import { Pipe, PipeTransform } from '@angular/core';
import { DateFormatUtility } from '../core-utilities';
import { DateFormats } from './date-formats.model';

/**
 * Pipe to format dates in the template.
 *
 * Usage with enum:
 * 1) Expose enum to template
 * DateFormats = DateFormats
 * 2) Use dateFormat pipe
 * {{ date | dateFormat: DateFormats.MonthYear }}
 *
 * Usage without enum:
 * {{ date | dateFormat: 'monthYear' }}
 *
 * The enums are string enums so you can use the string value instead.
 */
@Pipe({
    name: 'dateFormat',
    standalone: false
})
export class DatePipe implements PipeTransform {
    constructor(private dateFormatUtil: DateFormatUtility) {}

    /**
     * Transform the date to a PI formatted date
     *
     * @param value The date, this will come in as a string
     * @param args optional format type
     * @returns the new date string
     */
    transform(value: string, ...args: DateFormats[]): string {
        const date = new Date(value);
        if (args?.length > 0) {
            return this.transformWithCustomFormat(date, args);
        }

        return this.transformDate(date);
    }

    transformDate(date: Date) {
        return this.dateFormatUtil.transformDate(date);
    }

    transformWithCustomFormat(date: Date, formats: DateFormats[]) {
        const format = formats[0];
        switch (format) {
            case DateFormats.TwelveHourFormat: {
                return this.dateFormatUtil.getTimeIn12HourFormat(date);
            }
            case DateFormats.MonthDayYear: {
                return this.dateFormatUtil.getMonthDayYearFormat(date);
            }
            case DateFormats.DayMonthNameYear: {
                return this.dateFormatUtil.getDayMonthNameYearFormat(date);
            }
            case DateFormats.MonthNameDayYear: {
                return this.dateFormatUtil.getMonthNameDayYearFormat(date);
            }
            case DateFormats.MonthYear: {
                return this.dateFormatUtil.getMonthYearFormat(date);
            }
            default: {
                return this.transformDate(date);
            }
        }
    }
}
